import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import { X, CheckCircle, Fingerprint, Printer, Image as ImageIcon, Trash } from "@phosphor-icons/react";
import { BACKEND_URL } from "../lib/api";

const STATUS_STYLE = {
  RED: { text: "text-[#FF0033]", border: "border-[#FF0033]/60", glow: "border-glow-red", label: "UNPAID", hex: "#FF0033" },
  GREEN: { text: "text-[#00FF66]", border: "border-[#00FF66]/60", glow: "border-glow-green", label: "PAID", hex: "#00FF66" },
  PURPLE: { text: "text-[#9D00FF]", border: "border-[#9D00FF]/60", glow: "border-glow-purple", label: "REDEEMED", hex: "#9D00FF" },
};

export default function TicketModal({ ticket, event, open, onClose, onPay, onDelete }) {
  if (!open || !ticket) return null;
  const s = STATUS_STYLE[ticket.color_code] || STATUS_STYLE.RED;
  const downloadPdf = () => window.open(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/print.pdf`, "_blank");
  const downloadPng = () => window.open(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/image.png`, "_blank");

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-auto" data-testid="ticket-modal">
      <div className="relative w-full max-w-4xl">
        <button onClick={onClose} data-testid="ticket-modal-close" className="absolute -top-3 -right-3 sm:top-3 sm:right-3 z-20 p-2 bg-black border border-white/20 hover:bg-white hover:text-black transition-colors duration-200">
          <X size={18} />
        </button>

        <div className={`grid grid-cols-1 md:grid-cols-[1.6fr_1fr] bg-[#0A0A0C] border ${s.border} ${s.glow} overflow-hidden`}>
          {/* LEFT panel — pure art, no overlay */}
          <div className="relative aspect-[16/9] md:aspect-auto md:min-h-[480px] bg-black overflow-hidden">
            {event?.design_url ? (
              <img src={event.design_url} alt="ticket design" className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 aura-grid flex items-center justify-center">
                <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/25">No Design Art</div>
              </div>
            )}
            {/* Tiny AURA watermark in corner only */}
            <div className="absolute bottom-3 left-3 flex items-center gap-2 px-2 py-1 bg-black/60 backdrop-blur border border-white/10">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: s.hex }} />
              <div className="mono-font text-[9px] tracking-[0.3em] uppercase text-white">AURA · {event?.event_id}</div>
            </div>
          </div>

          {/* Tear line indicator */}
          <div aria-hidden className="md:hidden h-[2px] w-full" style={{
            backgroundImage: "linear-gradient(to right, rgba(255,255,255,0.35) 50%, transparent 50%)",
            backgroundSize: "8px 2px",
          }} />

          {/* RIGHT stub */}
          <div className="p-6 sm:p-7 flex flex-col gap-4 relative border-l border-dashed border-white/20">
            <div className="flex items-center justify-between">
              <div className={`inline-flex items-center gap-2 px-2 py-1 border ${s.border} ${s.text} mono-font text-[10px] tracking-widest`}>
                <Fingerprint size={12} weight="fill" /> {ticket.color_code} · {s.label}
              </div>
            </div>

            {/* Event details — compact, on the stub */}
            <div className="border border-white/10 p-3">
              <div className="mono-font text-[9px] tracking-[0.3em] uppercase text-white/40">Event</div>
              <div className="heading-font text-lg font-bold tracking-tight leading-tight mt-1 line-clamp-2">{event?.title}</div>
              {event?.host && (
                <div className="mt-2 mono-font text-[11px] text-white/70">
                  Hosted by <span className="text-white">{event.host}</span>
                </div>
              )}
              {(event?.date || event?.venue) && (
                <div className="mt-2 mono-font text-[11px] text-white/70 space-y-0.5">
                  {event?.date && <div>{event.date}</div>}
                  {event?.venue && <div className="truncate">{event.venue}</div>}
                </div>
              )}
            </div>

            <div>
              <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Ticket</div>
              <div className="mono-font text-sm text-white mt-1" data-testid="ticket-modal-id">{ticket.ticket_id}</div>
            </div>

            <div>
              <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Bearer</div>
              <div className="mono-font text-sm text-white mt-1">{ticket.buyer_name}</div>
              <div className="mono-font text-xs text-white/60 mt-1">{ticket.buyer_phone}</div>
            </div>

            <div>
              <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Distributed by</div>
              <div className="mono-font text-sm text-white mt-1">{ticket.member_name || "—"}</div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-white flex-shrink-0">
                <QRCodeCanvas value={ticket.token_hash} size={100} level="H" includeMargin={false} />
              </div>
              <div className="min-w-0">
                <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">SHA-256</div>
                <div className={`mono-font text-[13px] mt-1 break-all ${s.text}`}>{ticket.token_hash}</div>
              </div>
            </div>

            <div className="mt-auto grid grid-cols-2 gap-2 pt-2">
              <button onClick={downloadPng} data-testid="ticket-modal-png"
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-white/20 mono-font text-[11px] uppercase tracking-[0.2em] hover:border-white/60 transition-colors duration-200">
                <ImageIcon size={13} /> PNG
              </button>
              <button onClick={downloadPdf} data-testid="ticket-modal-print"
                className="inline-flex items-center justify-center gap-2 px-3 py-2.5 border border-white/20 mono-font text-[11px] uppercase tracking-[0.2em] hover:border-white/60 transition-colors duration-200">
                <Printer size={13} /> PDF
              </button>
              {ticket.color_code === "RED" && onPay && (
                <button onClick={onPay} data-testid="ticket-modal-pay"
                  className="col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#00FF66]/60 text-[#00FF66] mono-font text-xs uppercase tracking-[0.25em] hover:bg-[#00FF66] hover:text-black transition-colors duration-200">
                  <CheckCircle size={14} /> Confirm Payment
                </button>
              )}
              {onDelete && ticket.color_code !== "PURPLE" && (
                <button onClick={onDelete} data-testid="ticket-modal-delete"
                  className="col-span-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#FF0033]/50 text-[#FF0033]/80 mono-font text-[11px] uppercase tracking-[0.25em] hover:bg-[#FF0033] hover:text-white transition-colors duration-200">
                  <Trash size={13} /> Delete Ticket
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
